import { Translate } from '@components'

import { TableColumn } from '@billy/design-system'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { ProductType } from '@features/products/enums/productType'

import { SalesReportTableFormattedCurrency } from '../../elements/SalesReportTableFormattedCurrency'
import { SalesByItemReportTableAccessor } from '../../enums/salesByItemReportTableAccessor'
import { SalesByValueReportTableRow } from '../../types/salesByValueReportTableRow'
import { getProductTypeLabel } from '../../utils/getProductTypeLabel'
import { getSalesByValueReportAllSummary } from '../../utils/getSalesByValueReportAllSummary'
import { useSalesReportFilters } from '../useSalesReportFilters'
import { useTotalSales } from '../useTotalSales'
import { useSalesByItemReport } from './useSalesByItemReport'

export const useSalesByItemReportTableColumns = () => {
  const { t } = useTranslation()
  const [{ shouldShowVat }] = useSalesReportFilters()
  const { salesByItemReport } = useSalesByItemReport()

  const salesByItemReportAllSummary = useMemo(
    () => getSalesByValueReportAllSummary(salesByItemReport),
    [salesByItemReport],
  )

  const { grossTotalSales, netTotalSales } = useTotalSales(salesByItemReportAllSummary)

  const baseColumns: TableColumn<SalesByValueReportTableRow>[] = useMemo(
    () => [
      {
        accessor: SalesByItemReportTableAccessor.ItemName,
        Footer: <Translate value="sales_report.sales_by_item.table.footer.total_sales" />,
        Header: <Translate value="sales_report.sales_by_item.table.column.item" />,
        size: 'm',
        truncate: true,
      },
      {
        accessor: SalesByItemReportTableAccessor.ItemType,
        alignment: 'right',
        Cell: ({ value: itemType }: { value: ProductType }) => getProductTypeLabel(itemType, t),
        Header: <Translate value="sales_report.sales_by_item.table.column.item_type" />,
        size: 'xs',
        truncate: true,
      },
      {
        accessor: SalesByItemReportTableAccessor.ItemQuantity,
        alignment: 'right',
        Cell: ({ value: itemQuantity }: { value: number }) => itemQuantity,
        fixedSize: true,
        Header: <Translate value="sales_report.sales_by_item.table.column.item_quantity" />,
        size: 'xs',
      },
      {
        accessor: SalesByItemReportTableAccessor.NetTotal,
        alignment: 'right',
        Cell: ({ value }) => <SalesReportTableFormattedCurrency value={value} />,
        Footer: netTotalSales,
        Header: <Translate value="sales_report.sales_by_item.table.column.net_total" />,
        size: 'xs',
      },
      {
        accessor: SalesByItemReportTableAccessor.GrossTotal,
        alignment: 'right',
        Cell: ({ value }) => <SalesReportTableFormattedCurrency value={value} />,
        Footer: grossTotalSales,
        Header: <Translate value="sales_report.sales_by_item.table.column.gross_total" />,
        hidden: !shouldShowVat,
        size: 'xs',
      },
    ],
    [grossTotalSales, netTotalSales, shouldShowVat, t],
  )

  return baseColumns.filter((column) => !column.hidden)
}
